import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Loader2, Sparkles, Languages, Heart, CheckCircle2, AlertTriangle, Play } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  {
    id: 'ai_content_gen',
    icon: Sparkles,
    label: 'Content Summaries',
    description:
      'Auto-summarize pending content suggestions for the weekly digest email.',
    testFeature: 'summary',
    testLabel: 'Summarize sample article',
  },
  {
    id: 'ai_translation',
    icon: Languages,
    label: 'Translation',
    description: 'Translate posts & blog articles into the reader\u2019s language.',
    testFeature: 'translation',
    testLabel: 'Translate \u201CHello\u201D to Spanish',
  },
  {
    id: 'ai_health_insights',
    icon: Heart,
    label: 'Health Insights',
    description:
      'Breed-aware Q&A on dog health & behavior (with a veterinary disclaimer).',
    testFeature: 'health_qa',
    testLabel: 'Sample Labrador joint-health Q',
  },
];

export default function AiFeaturesPanel() {
  const [flags, setFlags] = useState({});
  const [allFlags, setAllFlags] = useState([]);
  const [testing, setTesting] = useState({});
  const [results, setResults] = useState({});

  const loadFlags = async () => {
    try {
      const res = await axios.get(`${API}/community/admin/flags`, {
        withCredentials: true,
      });
      const list = res.data?.flags || [];
      setAllFlags(list);
      const map = {};
      list.forEach((f) => {
        map[f.id] = f.enabled;
      });
      setFlags(map);
    } catch (err) {
      toast.error('Failed to load feature flags');
    }
  };

  useEffect(() => {
    loadFlags();
  }, []);

  const toggleFlag = async (id, next) => {
    setFlags((prev) => ({ ...prev, [id]: next }));
    try {
      await axios.post(
        `${API}/community/admin/flags/${id}`,
        { enabled: next },
        { withCredentials: true }
      );
      toast.success(`${id} is now ${next ? 'ON' : 'OFF'}`);
    } catch (err) {
      setFlags((prev) => ({ ...prev, [id]: !next }));
      toast.error('Toggle failed');
    }
  };

  const runTest = async (feature) => {
    setTesting((prev) => ({ ...prev, [feature.id]: true }));
    setResults((prev) => ({ ...prev, [feature.id]: null }));
    try {
      const payload = {
        summary: {
          title: 'Why Your Dog Tilts Its Head',
          description:
            "Many dogs tilt their heads when they hear unfamiliar sounds. Here's what researchers think is going on.",
        },
        translation: { text: 'Hello friends, welcome to the community!', target_lang: 'Spanish' },
        health_qa: {
          breed: 'Labrador Retriever',
          question: 'What joint issues should I watch for in my senior Lab?',
        },
      }[feature.testFeature];

      const res = await axios.post(
        `${API}/ai/admin/test`,
        { feature: feature.testFeature, payload },
        { withCredentials: true }
      );
      setResults((prev) => ({
        ...prev,
        [feature.id]: { ok: true, text: res.data?.result || '' },
      }));
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [feature.id]: {
          ok: false,
          text: err?.response?.data?.detail || err.message || 'Test failed',
        },
      }));
    } finally {
      setTesting((prev) => ({ ...prev, [feature.id]: false }));
    }
  };

  return (
    <Card data-testid="ai-features-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          AI Features
        </CardTitle>
        <CardDescription>
          Toggle each AI capability on/off. The &quot;Run test&quot; button bypasses the flag
          to verify live connectivity to Claude Sonnet 4.5.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {FEATURES.map((f) => {
          const Icon = f.icon;
          const enabled = !!flags[f.id];
          const result = results[f.id];
          return (
            <div
              key={f.id}
              className="border rounded-lg p-4 space-y-3"
              data-testid={`ai-feature-${f.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <Icon className="w-5 h-5 mt-0.5 text-muted-foreground" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{f.label}</span>
                      <Badge variant={enabled ? 'default' : 'secondary'}>
                        {enabled ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{f.description}</p>
                  </div>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={(v) => toggleFlag(f.id, v)}
                  data-testid={`toggle-${f.id}`}
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runTest(f)}
                  disabled={testing[f.id]}
                  data-testid={`test-${f.id}`}
                >
                  {testing[f.id] ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Play className="w-3 h-3 mr-1" />
                  )}
                  {f.testLabel}
                </Button>
              </div>

              {result && (
                <div
                  className={`text-sm rounded border p-3 ${
                    result.ok
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                  data-testid={`result-${f.id}`}
                >
                  <div className="flex items-center gap-1 font-medium mb-1">
                    {result.ok ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Test passed
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        Test failed
                      </>
                    )}
                  </div>
                  <pre className="text-xs whitespace-pre-wrap text-muted-foreground">
                    {result.text}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
